import { useState, useMemo } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useShowHiddenNotes } from './useShowHiddenNotes'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getNotes } from '@/data/notes/getNotes'
import { getUsers } from '@/data/users/getUsers'
import { deleteNote } from '@/data/notes/deleteNote'
import { queryKeys } from '@/lib/queryKeys'
import { GOVUK_COLOURS } from '@/components/govuk/colours'
import { useCurrentUser } from './CurrentUserContext'
import { getUserColour } from '@/lib/userColour'
import { EyeOff } from 'lucide-react'
import 'govuk-frontend/dist/govuk/govuk-frontend.min.css'

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.floor(months / 12)}y ago`
}

export function NotesSidebar() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const routerState = useRouterState()
  const { currentUser } = useCurrentUser()
  const [search, setSearch] = useState('')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const isHarry = currentUser === 'Harry'
  const showHidden = useShowHiddenNotes()

  const { data: notes = [], isLoading } = useQuery({
    queryKey: queryKeys.notes.lists(),
    queryFn: () => getNotes({ data: { currentUser: currentUser ?? '' } }),
  })

  const { data: users = [] } = useQuery({
    queryKey: queryKeys.users.list(),
    queryFn: () => getUsers(),
  })

  const deleteMutation = useMutation({
    mutationFn: (noteId: string) => deleteNote({ data: { noteId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notes.all })
    },
  })

  const currentPath = routerState.location.pathname
  const activeNoteId =
    currentPath.startsWith('/notes/') && currentPath !== '/notes/new'
      ? currentPath.slice('/notes/'.length)
      : null

  const filtered = useMemo(() => {
    const visible = showHidden ? notes : notes.filter((n) => !n.hidden)
    const q = search.toLowerCase().trim()
    if (!q) return visible
    return visible.filter((n) => n.title.toLowerCase().includes(q))
  }, [notes, search, showHidden])

  function handleDelete(id: string) {
    deleteMutation.mutate(id)
    setDeletingId(null)
    if (activeNoteId === id) navigate({ to: '/notes/new' })
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', fontFamily: 'inherit' }}>
      {/* Header */}
      <div
        style={{
          padding: '10px 12px',
          borderBottom: `1px solid ${GOVUK_COLOURS.midGrey}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          gap: '8px',
        }}
      >
        <span style={{ fontWeight: 700, fontSize: '17px' }}>Notes</span>
        <button
          type="button"
          onClick={() => navigate({ to: '/notes/new' })}
          title="New note"
          style={{
            background: GOVUK_COLOURS.blue,
            border: 'none',
            borderRadius: '4px',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '20px',
            fontWeight: 400,
            lineHeight: 1,
            padding: '2px 10px 4px',
            flexShrink: 0,
          }}
        >
          +
        </button>
      </div>

      {/* Search */}
      <div
        style={{
          padding: '10px 12px',
          borderBottom: `1px solid ${GOVUK_COLOURS.midGrey}`,
          flexShrink: 0,
        }}
      >
        <input
          type="text"
          placeholder="Search notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: '100%',
            padding: '6px 10px',
            border: `1px solid ${GOVUK_COLOURS.midGrey}`,
            borderRadius: '2px',
            fontSize: '14px',
            boxSizing: 'border-box',
            outline: 'none',
            fontFamily: 'inherit',
          }}
        />
      </div>

      {/* Note list */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}>
        {isLoading && (
          <div style={{ padding: '20px 16px', color: GOVUK_COLOURS.darkGrey, fontSize: '14px' }}>
            Loading…
          </div>
        )}

        {!isLoading && filtered.length === 0 && (
          <div
            style={{
              padding: '24px 16px',
              color: GOVUK_COLOURS.darkGrey,
              fontSize: '14px',
              textAlign: 'center',
            }}
          >
            {notes.length === 0
              ? 'No notes yet.\nClick + to create one.'
              : `No results for "${search}"`}
          </div>
        )}

        {filtered.map((note) => {
          const isActive = note.id === activeNoteId
          const isDeleting = deletingId === note.id
          const dotColour = getUserColour(note.lastEditedBy, users, currentUser ?? '')

          return (
            <div
              key={note.id}
              onClick={() => {
                if (!isDeleting) navigate({ to: '/notes/$noteId', params: { noteId: note.id } })
              }}
              style={{
                padding: '10px 12px',
                cursor: 'pointer',
                borderBottom: `1px solid ${GOVUK_COLOURS.midGrey}`,
                borderLeft: isActive ? `3px solid ${GOVUK_COLOURS.blue}` : '3px solid transparent',
                backgroundColor: isActive ? '#dce9f9' : 'transparent',
                transition: 'background-color 0.1s',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!isActive)
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = '#f3f2f1'
              }}
              onMouseLeave={(e) => {
                if (!isActive)
                  (e.currentTarget as HTMLDivElement).style.backgroundColor = 'transparent'
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {isHarry && note.hidden && (
                  <EyeOff size={12} style={{ opacity: 0.4, flexShrink: 0 }} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {note.title || 'Untitled'}
                </span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  {note.lastEditedBy && (
                    <span
                      title={note.lastEditedBy}
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        backgroundColor: dotColour,
                        flexShrink: 0,
                        display: 'inline-block',
                      }}
                    />
                  )}
                  <span style={{ fontSize: '11px', color: GOVUK_COLOURS.darkGrey }}>
                    {timeAgo(note.updatedAt)}
                  </span>
                </div>

                {isDeleting ? (
                  <span style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    <span style={{ fontSize: '11px', color: GOVUK_COLOURS.red, marginRight: '2px' }}>
                      Delete?
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDelete(note.id)
                      }}
                      style={deleteBtnStyle(GOVUK_COLOURS.red)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeletingId(null)
                      }}
                      style={deleteBtnStyle(GOVUK_COLOURS.darkGrey)}
                    >
                      No
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDeletingId(note.id)
                    }}
                    title="Delete note"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: GOVUK_COLOURS.darkGrey,
                      fontSize: '13px',
                      padding: '1px 4px',
                      opacity: 0.6,
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function deleteBtnStyle(colour: string): React.CSSProperties {
  return {
    background: 'none',
    border: `1px solid ${colour}`,
    borderRadius: '2px',
    color: colour,
    cursor: 'pointer',
    fontSize: '11px',
    padding: '1px 5px',
    lineHeight: 1.4,
  }
}
